import {
  useState,
  useEffect,
  useRef,
  type ReactNode,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";

export interface EditableRowProps {
  isEditing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onDelete: () => void;
  hoverable?: boolean;
  renderView: () => ReactNode;
  renderEdit: () => ReactNode;
}

export function EditableRow({
  isEditing,
  onEditStart,
  onEditEnd,
  onDelete,
  hoverable = true,
  renderView,
  renderEdit,
}: EditableRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState<boolean>(false);
  const [hovered, setHovered] = useState<boolean>(false);

  useEffect(() => {
    if (!isEditing) return;
    const handleMouseDown = (e: MouseEvent) => {
      const rowEl = rowRef.current;
      if (rowEl && !rowEl.contains(e.target as Node)) {
        onEditEnd();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isEditing, onEditEnd]);

  const handleRowClick = () => {
    setRevealed((v) => !v);
  };

  const showControls =
    hoverable !== false && (isEditing || hovered || revealed);

  const stopProp = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  const iconButtonStyle: CSSProperties = {
    width: "22px",
    height: "22px",
    border: "1px solid var(--paper-rule)",
    background: "transparent",
    color: "var(--paper-ink-2)",
    cursor: "pointer",
    font: "400 14px 'JetBrains Mono', monospace",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    flexShrink: 0,
  };

  const doneButtonStyle: CSSProperties = {
    font: "500 11px 'JetBrains Mono', monospace",
    letterSpacing: ".08em",
    padding: "2px 8px",
    border: "1px solid var(--paper-rule)",
    background: "var(--paper-ink)",
    color: "var(--paper-bg)",
    cursor: "pointer",
  };

  const controlsStyle: CSSProperties = {
    position: "absolute",
    right: "4px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  return (
    <div
      ref={rowRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleRowClick}
      style={{ position: "relative" }}
    >
      {isEditing ? renderEdit() : renderView()}
      {showControls && (
        <div style={controlsStyle}>
          {isEditing ? (
            <button
              type="button"
              onClick={(e) => {
                stopProp(e);
                onEditEnd();
              }}
              style={doneButtonStyle}
            >
              Готово
            </button>
          ) : (
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                stopProp(e);
                onEditStart();
              }}
              style={iconButtonStyle}
            >
              ✎
            </button>
          )}
          <button
            type="button"
            aria-label="Delete"
            onClick={(e) => {
              stopProp(e);
              onDelete();
            }}
            style={iconButtonStyle}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
